import {
  useCaseParties,
  groupByParty,
  type GroupedParty,
} from "../../hooks/useCaseParties";

interface Props {
  caseId: string;
}

/**
 * Battlemap — parties on the field.
 *
 * v0 layout: plaintiffs on the left, defendants on the right, third
 * parties / witnesses / intervenors below. Each party shows its
 * display name + all the capacities they appear in.
 *
 * Real spatial graph layout (force-directed nodes, relationship lines)
 * is later work — once Raven recon dossiers feed in adjacent parties
 * (not just named-on-caption parties).
 */
function Battlemap({ caseId }: Props) {
  const { data: rows, isLoading, error } = useCaseParties(caseId);

  let body: React.ReactNode;
  let status: string;

  if (isLoading) {
    body = <Loading />;
    status = "loading";
  } else if (error) {
    body = <ErrorState message={error.message} />;
    status = "the lines are cut";
  } else if (!rows || rows.length === 0) {
    body = <EmptyState />;
    status = "no parties yet";
  } else {
    const parties = groupByParty(rows);
    body = <PartiesField parties={parties} />;
    status = `${parties.length} ${parties.length === 1 ? "party" : "parties"}`;
  }

  return (
    <div className="relative flex h-full min-h-[640px] flex-col rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <PanelHeader label="battlemap" status={status} />
      <div className="flex-1 overflow-auto">{body}</div>
    </div>
  );
}

function PanelHeader({ label, status }: { label: string; status: string }) {
  return (
    <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em]">
      <span className="text-bone-dim">{label}</span>
      <span className="text-bone-mute/70">// {status}</span>
    </div>
  );
}

function PartiesField({ parties }: { parties: GroupedParty[] }) {
  const plaintiffs = parties.filter((p) => p.role === "plaintiff");
  const defendants = parties.filter((p) => p.role === "defendant");
  const others = parties.filter(
    (p) => p.role !== "plaintiff" && p.role !== "defendant",
  );

  return (
    <div className="flex h-full flex-col gap-4 p-5">
      <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-2">
        <PartyColumn label="plaintiffs" accent="blue" parties={plaintiffs} />
        <PartyColumn label="defendants" accent="ember" parties={defendants} />
      </div>
      {others.length > 0 ? (
        <PartyColumn label="other parties" accent="bone" parties={others} />
      ) : null}
    </div>
  );
}

function PartyColumn({
  label,
  accent,
  parties,
}: {
  label: string;
  accent: "blue" | "ember" | "bone";
  parties: GroupedParty[];
}) {
  const accentText =
    accent === "blue"
      ? "text-blue-bright"
      : accent === "ember"
        ? "text-ember-bright"
        : "text-bone";

  const borderHover =
    accent === "blue"
      ? "hover:border-blue-bright/60"
      : accent === "ember"
        ? "hover:border-ember-bright/60"
        : "hover:border-bone-mute/60";

  return (
    <div>
      <p
        className={`mb-3 font-display text-base font-light uppercase tracking-[0.16em] ${accentText}`}
      >
        // {label} // {parties.length}
      </p>
      {parties.length === 0 ? (
        <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute/60">
          (none on the field)
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {parties.map((p) => (
            <PartyCard key={p.party_id} party={p} borderHover={borderHover} />
          ))}
        </div>
      )}
    </div>
  );
}

function PartyCard({
  party,
  borderHover,
}: {
  party: GroupedParty;
  borderHover: string;
}) {
  const sortedCapacities = [...party.capacities].sort();
  return (
    <div
      className={`rounded border border-steel-dark bg-iron-2/60 px-4 py-3 backdrop-blur-sm transition-colors ${borderHover}`}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="font-display text-base font-light text-bone">
          {party.display_name}
        </p>
        <span className="shrink-0 rounded border border-bone-mute/40 px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">
          {party.party_type}
        </span>
      </div>
      {sortedCapacities.length > 0 ? (
        <ul className="mt-2 space-y-0.5">
          {sortedCapacities.map((cap) => (
            <li
              key={cap}
              className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute"
            >
              · {cap}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function Loading() {
  return (
    <div className="flex h-full items-center justify-center">
      <p className="flex items-center gap-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute">
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow" />
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:200ms]" />
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:400ms]" />
        gathering the field
      </p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="p-5">
      <p className="font-display text-base font-light uppercase tracking-[0.16em] text-ember-bright">
        // the lines are cut
      </p>
      <p className="mt-2 font-display text-base text-bone-dim">{message}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full items-center justify-center p-5">
      <div className="max-w-md text-center">
        <p className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute">
          // no parties on the field
        </p>
        <p className="mt-2 font-display text-sm text-bone-dim">
          Parties populate when a filing with a caption is ingested. Older
          filings may not have included party data — re-ingest the operative
          pleading to backfill.
        </p>
      </div>
    </div>
  );
}

export default Battlemap;
